import styled from '@emotion/styled';
import { Trans, useTranslation } from 'react-i18next';
import { CodeBlock, Button, Text, tokens } from '@hotjar/hotsauce';
import type { Site } from '@hotjar/site';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { useModalTrackingCode } from '@hotjar/webapp/hooks/useModalTrackingCode';
import { Code } from '@hotjar/webapp/components/Code';
import { List, ListItem } from '../components/StepsList';

interface NPMInstallProps {
  site: Site;
}

const Snippet = styled(CodeBlock)`
  min-width: 450px;
`;

const Instructions = styled(Text)`
  display: block;
  margin-top: ${tokens.spacer.x0};

  strong {
    font-weight: 600;
  }
`;

const SecondaryCTA = styled(Button)`
  margin-left: ${tokens.spacer.x1};
`;

export const NPMInstallation = ({ site }: NPMInstallProps): JSX.Element => {
  const { t } = useTranslation();
  const track = useTrack();
  const trackClick = (element: string) => track.click(element, 'NPM Installation', {});
  const { show: showTrackingCodeModal } = useModalTrackingCode();
  const onVerifyClick = () => {
    showTrackingCodeModal(site, 2, 'Setup Hotjar Component');
    track.click('npm', 'Type of Installation', {});
  };

  return (
    <List>
      <ListItem>
        <span>
          <Instructions>{t('activation:setup-wizard.npm-installation.install')}</Instructions>
          <br />
          <Code>yarn add @hotjar/browser</Code>
        </span>
      </ListItem>
      <ListItem>
        <span>
          <Instructions>
            <Trans i18nKey="activation:setup-wizard.npm-installation.initialize">
              Initialize the script specifying your site ID: <strong>{{ siteId: site.id }}</strong>
            </Trans>
          </Instructions>
          <br />
          <Snippet
            allowCopy
            copyText={t('activation:tracking-code.installation.copy-btn-text')}
            copyFeedback={t('activation:tracking-code.installation.copy-btn-feedback')}
            content={`import Hotjar from '@hotjar/browser';

const siteId = ${site.id};
const hotjarVersion = 6;

Hotjar.init(siteId, hotjarVersion);`}
          />
        </span>
      </ListItem>
      <ListItem>
        <Button
          onClick={onVerifyClick}
          loadingText={t('common:registration.select-signup-method.submit-btn-loading')}
        >
          {t('activation:tracking-code.installation.verify')}
        </Button>
        <SecondaryCTA
          as="a"
          kind="secondary"
          isExternal
          href="https://www.npmjs.com/package/@hotjar/browser"
          icon="open_new_window"
          onClick={() => trackClick('View npm package')}
        >
          {t('activation:setup-wizard.npm-installation.secondary-cta')}
        </SecondaryCTA>
      </ListItem>
    </List>
  );
};
